import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import { CheckCircle2, ChevronRight } from 'lucide-react';
import { Button } from "../components/ui/button";
import { useConsultationModal } from '../context/ConsultationModalContext';

// Futuristic AI-style SVG Icons
const IconBanking = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <path d="M3 21H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M3 10H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M12 3L3 10H21L12 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M5 10V21M9 10V21M15 10V21M19 10V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="12" cy="15" r="2" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
);

const IconMigration = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <rect x="2" y="4" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="14" y="14" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M10 7H14C16 7 17 8 17 10V14" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M14 11L17 14L20 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="6" cy="7" r="1" fill="currentColor"/>
        <circle cx="18" cy="17" r="1" fill="currentColor"/>
    </svg>
);

const IconVMware = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M10 6.5H14M17.5 10V14M10 17.5H14M6.5 10V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const IconDatabase = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <ellipse cx="12" cy="5" rx="7" ry="3" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M5 5V19C5 20.657 8.134 22 12 22C15.866 22 19 20.657 19 19V5" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M5 10C5 11.657 8.134 13 12 13C15.866 13 19 11.657 19 10" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M5 14.5C5 16.157 8.134 17.5 12 17.5C15.866 17.5 19 16.157 19 14.5" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
);

const IconSOC = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M12 7V12L15 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M12 3V5M12 19V21M3 12H5M19 12H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const IconDR = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <circle cx="7" cy="12" r="4" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="17" cy="12" r="4" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M11 12H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M11 10L13 12L11 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M7 8V5M17 8V5M7 16V19M17 16V19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const IconKubernetes = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <path d="M12 2L21 7V17L12 22L3 17V7L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M12 9V6M12 18V15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M9.5 10.5L7 8M17 16L14.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M14.5 10.5L17 8M7 16L9.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const IconAI = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M4 20C4 16.686 7.582 14 12 14C16.418 14 20 16.686 20 20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M12 4V2M8 5L6 3M16 5L18 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="10" cy="7" r="1" fill="currentColor"/>
        <circle cx="14" cy="7" r="1" fill="currentColor"/>
    </svg>
);

const IconIdentity = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <rect x="3" y="6" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="12" cy="13" r="3" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M12 10V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M9.5 13H8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M15.5 13H14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M12 16V17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M9 6V4H15V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const IconConsulting = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <circle cx="9" cy="7" r="3" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="15" cy="7" r="3" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M3 19C3 15.686 5.239 13 9 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M21 19C21 15.686 18.761 13 15 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M9 13C9 13 10.5 15 12 15C13.5 15 15 13 15 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M12 15V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M9 18H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const Solutions = () => {
    const [activeSection, setActiveSection] = useState('banking');
    const { openModal } = useConsultationModal();
    const location = useLocation();

    // Handle hash navigation from footer links
    useEffect(() => {
        const hash = location.hash.replace('#', '');
        if (hash && ['banking', 'ai', 'migration', 'database', 'security', 'resilience', 'modern', 'identity', 'consulting'].includes(hash)) {
            setActiveSection(hash);
        }
    }, [location.hash]);

    const sections = [
        { id: 'banking', label: 'Banking & Finance', Icon: IconBanking },
        { id: 'ai', label: 'AI & Analytics', Icon: IconAI },
        { id: 'migration', label: 'Migration', Icon: IconMigration },
        { id: 'database', label: 'Database', Icon: IconDatabase },
        { id: 'security', label: 'Security Ops', Icon: IconSOC },
        { id: 'resilience', label: 'DR & Continuity', Icon: IconDR },
        { id: 'modern', label: 'Modernization', Icon: IconKubernetes },
        { id: 'identity', label: 'Identity', Icon: IconIdentity },
        { id: 'consulting', label: 'Consulting', Icon: IconConsulting },
    ];

    return (
        <Layout>
            <SEO page="solutions" />
            <PageHeader
                title="EverestCloud" 
                highlight="Solutions"
                description="Secure, compliant, and purpose-built solutions for banks, government, and large enterprises—covering cloud, migration, security, and operations."
            />

            <section className="py-16 bg-white dark:bg-slate-950 transition-colors duration-500">
                <div className="container mx-auto px-4">

                    {/* Section Navigation */}
                    <div className="flex flex-wrap justify-center gap-2 mb-12">
                        {sections.map((section) => (
                            <button
                                key={section.id}
                                onClick={() => setActiveSection(section.id)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                                    activeSection === section.id
                                        ? 'bg-accent text-black shadow-lg'
                                        : 'bg-slate-100 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                            >
                                <div className="w-4 h-4">
                                    <section.Icon />
                                </div>
                                {section.label}
                            </button>
                        ))}
                    </div>

                    {/* Content Sections */}
                    <div className="space-y-8">
                        
                        {activeSection === 'banking' && (
                            <div className="animate-in fade-in-50 duration-500">
                                <SolutionCard
                                    Icon={IconBanking}
                                    title="Banking & Financial Services Cloud"
                                    subtitle="Purpose-built solutions for core banking and financial platforms"
                                    leftTitle="Capabilities"
                                    leftItems={[
                                        "Dedicated and shared cloud with strict isolation",
                                        "Multi-AZ business continuity",
                                        "Secure network segmentation",
                                        "Privileged access controls and audit trails",
                                        "Nepal-only data residency"
                                    ]}
                                    rightTitle="Use Cases"
                                    rightTags={["Core Banking", "Digital Channels", "Payments", "AML", "Treasury", "DR Platforms"]}
                                />
                            </div>
                        )}

                        {activeSection === 'migration' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <SolutionCard
                                    Icon={IconMigration}
                                    title="Core Banking Migration Frameworks"
                                    subtitle="Specialized, risk-controlled migration methodologies for banking systems"
                                    leftTitle="Supported Platforms"
                                    leftItems={[
                                        "Core Banking Systems",
                                        "Payment & Switching Platforms",
                                        "Treasury & Trade Finance Systems"
                                    ]}
                                    rightTitle="Framework Includes"
                                    rightItems={[
                                        "Pre-migration assessment & dependency mapping",
                                        "Parallel run and reconciliation models",
                                        "Controlled cutover with rollback strategy",
                                        "Post-migration performance validation"
                                    ]}
                                    outcome="Zero-data-loss migration with minimal business disruption"
                                />

                                <SolutionCard
                                    Icon={IconVMware}
                                    title="VMware to EverestCloud Migration"
                                    subtitle="Structured migration from legacy virtualization platforms to EverestCloud"
                                    leftTitle="Includes"
                                    leftItems={[
                                        "Inventory and workload classification",
                                        "Compatibility and performance assessment",
                                        "Lift-and-shift or optimize-and-migrate options",
                                        "License optimization and cost rationalization"
                                    ]}
                                    rightTitle="Benefits"
                                    rightItems={[
                                        "Reduced licensing risk",
                                        "Improved operational efficiency",
                                        "Modern cloud operating model"
                                    ]}
                                />
                            </div>
                        )}

                        {activeSection === 'database' && (
                            <div className="animate-in fade-in-50 duration-500">
                                <SolutionCard
                                    Icon={IconDatabase}
                                    title="Database Replatforming & Modernization"
                                    subtitle="Secure and compliant database transformation services"
                                    leftTitle="Covers"
                                    leftItems={[
                                        "Database replatforming to cloud-optimized architectures",
                                        "Performance tuning and storage optimization",
                                        "Backup, replication, and DR design",
                                        "Encryption and access hardening"
                                    ]}
                                    rightTitle="Ideal For"
                                    rightTags={["Mission-critical databases", "Reporting systems", "Analytics platforms"]}
                                />
                            </div>
                        )}

                        {activeSection === 'security' && (
                            <div className="animate-in fade-in-50 duration-500">
                                <SolutionCard
                                    Icon={IconSOC}
                                    title="Security Operations Solutions (SOC & VOC)"
                                    subtitle="Centralized security monitoring and risk management"
                                    leftTitle="Security Operations Center (SOC)"
                                    leftItems={[
                                        "24×7 threat monitoring",
                                        "Incident detection and response",
                                        "Log correlation and alerting"
                                    ]}
                                    rightTitle="Vulnerability Operations Center (VOC)"
                                    rightItems={[
                                        "Continuous vulnerability assessment",
                                        "Risk scoring and remediation tracking",
                                        "Compliance-aligned reporting"
                                    ]}
                                />
                            </div>
                        )}

                        {activeSection === 'resilience' && (
                            <div className="animate-in fade-in-50 duration-500">
                                <SolutionCard
                                    Icon={IconDR}
                                    title="Business Continuity & Disaster Recovery"
                                    subtitle="High-availability solutions designed for zero-tolerance workloads"
                                    leftTitle="Capabilities"
                                    leftItems={[
                                        "Multi-Availability Zone architecture",
                                        "Automated failover and failback",
                                        "Defined RPO/RTO backed by SLA",
                                        "Periodic DR drills and reporting"
                                    ]}
                                />
                            </div>
                        )}

                        {activeSection === 'modern' && (
                            <div className="animate-in fade-in-50 duration-500">
                                <SolutionCard
                                    Icon={IconKubernetes}
                                    title="Application Modernization & Kubernetes Platforms"
                                    subtitle="Enable cloud-native and containerized workloads"
                                    leftTitle="Includes"
                                    leftItems={[
                                        "Managed Kubernetes platforms",
                                        "Secure CI/CD-ready environments",
                                        "Network isolation and workload policies",
                                        "Integrated monitoring and observability"
                                    ]}
                                />
                            </div>
                        )}

                        {activeSection === 'ai' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <SolutionCard
                                    Icon={IconAI}
                                    title="Built for Modern AI & Data Workloads"
                                    subtitle="Full lifecycle support for data-driven workloads"
                                    leftTitle="Capabilities"
                                    leftItems={[
                                        "Model training and fine-tuning",
                                        "Inference at scale",
                                        "High-volume data processing",
                                        "Low-latency analytics",
                                        "Parallel compute workloads"
                                    ]}
                                    rightTitle="Platform Features"
                                    rightItems={[
                                        "Dedicated GPU clusters",
                                        "High-bandwidth storage",
                                        "Optimized networking",
                                        "Consistent performance under peak load"
                                    ]}
                                />
                                <SolutionCard
                                    Icon={IconAI}
                                    title="Dedicated GPU & Accelerated Compute Platforms"
                                    subtitle="Single-tenant and multi-tenant GPU compute environments"
                                    leftTitle="Guarantees"
                                    leftItems={[
                                        "Isolated GPU clusters",
                                        "Performance-guaranteed resources",
                                        "Independent scaling from general workloads",
                                        "Enterprise-grade security"
                                    ]}
                                    rightTitle="Optimized For"
                                    rightTags={["Deep Learning", "Large Language Models (LLMs)", "Computer Vision", "NLP", "Scientific Simulations", "Financial Risk Modeling"]}
                                />
                            </div>
                        )}

                        {activeSection === 'identity' && (
                            <div className="animate-in fade-in-50 duration-500">
                                <SolutionCard
                                    Icon={IconIdentity}
                                    title="Secure Access & Identity Solutions"
                                    subtitle="Zero-trust access across all cloud and enterprise systems"
                                    leftTitle="Capabilities"
                                    leftItems={[
                                        "Privileged access management",
                                        "Multi-factor authentication",
                                        "Role-based access control",
                                        "Full session logging and audit trails"
                                    ]}
                                />
                            </div>
                        )}

                        {activeSection === 'consulting' && (
                            <div className="animate-in fade-in-50 duration-500">
                                <SolutionCard
                                    Icon={IconConsulting}
                                    title="Consulting & Managed Services"
                                    subtitle="Enterprise-grade operational support and advisory services"
                                    leftTitle="Consulting Services"
                                    leftItems={[
                                        "Cloud strategy & architecture design",
                                        "Regulatory and compliance alignment",
                                        "Capacity planning and performance engineering",
                                        "Security architecture and audits"
                                    ]}
                                    rightTitle="Managed Services"
                                    rightItems={[
                                        "24×7 NOC operations (L1/L2/L3)",
                                        "Platform monitoring and patching",
                                        "Incident, change, and problem management"
                                    ]}
                                />
                            </div>
                        )}

                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-slate-50 dark:bg-slate-900/50 transition-colors duration-500">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                        Ready to Transform Your Infrastructure?
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-400 mb-8 max-w-2xl mx-auto">
                        Our solutions architects are ready to design a tailored solution for your enterprise needs.
                    </p>
                    <Button 
                        className="h-14 px-10 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-full shadow-lg"
                        onClick={() => openModal('get_started')}
                    >
                        Get Started Today
                        <ChevronRight className="ml-2 h-5 w-5" />
                    </Button>
                </div>
            </section>
        </Layout>
    );
};

// Unified Solution Card Component
const SolutionCard = ({ Icon, title, subtitle, leftTitle, leftItems, leftTags, rightTitle, rightItems, rightTags, outcome }) => (
    <div className="p-8 rounded-2xl bg-slate-50 dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800">
        <div className="flex items-start gap-4 mb-6">
            <div className="p-3 rounded-xl shrink-0 w-12 h-12 bg-accent/10 text-accent">
                <Icon />
            </div>
            <div>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white">{title}</h3>
                <p className="text-accent font-medium">{subtitle}</p>
            </div>
        </div>

        <div className={`grid ${rightTitle ? 'md:grid-cols-2' : 'md:grid-cols-1'} gap-8`}>
            {/* Left Column */}
            {(leftItems || leftTags) && (
                <div>
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-4">{leftTitle}</h4>
                    {leftItems && (
                        <ul className="space-y-3">
                            {leftItems.map((item, i) => (
                                <li key={i} className="flex items-start gap-3 text-slate-600 dark:text-slate-300">
                                    <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5 text-accent" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {leftTags && (
                        <div className="flex flex-wrap gap-2">
                            {leftTags.map((item, i) => (
                                <span key={i} className="px-3 py-1.5 rounded-full text-sm font-medium bg-accent/10 text-accent">
                                    {item}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Right Column */}
            {(rightItems || rightTags) && (
                <div>
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-4">{rightTitle}</h4>
                    {rightItems && (
                        <ul className="space-y-3">
                            {rightItems.map((item, i) => (
                                <li key={i} className="flex items-start gap-3 text-slate-600 dark:text-slate-300">
                                    <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5 text-accent" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {rightTags && (
                        <div className="flex flex-wrap gap-2">
                            {rightTags.map((item, i) => (
                                <span key={i} className="px-3 py-1.5 rounded-full text-sm font-medium bg-accent/10 text-accent">
                                    {item}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>

        {outcome && (
            <div className="mt-8 p-4 rounded-xl bg-accent/10 border border-accent/20">
                <h4 className="text-sm font-bold text-accent uppercase tracking-wider mb-2">Outcome</h4>
                <p className="text-slate-700 dark:text-slate-200 font-medium">{outcome}</p>
            </div>
        )}
    </div>
);

export default Solutions;
